import { convertHexToRgba } from "./../utils/convertHexToRgba";
import BitmapData = createjs.BitmapData;

import { CREATEJS_UTILITIES_LOGGER } from "../../../../../index";
import { getLogger } from "@goodgamestudios/logger";
import Graphics = createjs.Graphics;
const logger = getLogger(CREATEJS_UTILITIES_LOGGER);

(createjs.Graphics.prototype as any).p = (createjs.Graphics.prototype as any).decodePath = function (str:any, _x:number, _y:number) {
    if (!_x) _x = 0;
    if (!_y) _y = 0;
    var instructions = [this.moveTo, this.lineTo, this.quadraticCurveTo, this.bezierCurveTo, this.closePath];
    var paramCount = [2, 2, 4, 6, 0];
    var i = 0, l = str.length;
    var params = [];
    var x = 0, y = 0;
    var base64:any = createjs.Graphics.BASE_64;

    while (i < l) {
        var c = str.charAt(i);
        var n = base64[c];
        var fi = n >> 3; // highest order bits 1-3 code for operation.
        var f = instructions[fi];
        // check that we have a valid instruction & that the unused bits are empty:
        if (!f || (n & 3)) { throw ("bad path data (@" + i + "): " + c); }
        var pl = paramCount[fi];
        if (!fi) { x = y = 0; } // move operations reset the position.
        params.length = 0;
        i++;
        var charCount = (n >> 2 & 1) + 2;  // 4th header bit indicates number size for this operation.
        for (var p = 0; p < pl; p++) {
            var num = base64[str.charAt(i)];
            var sign = (num >> 5) ? -1 : 1;
            num = ((num & 31) << 6) | (base64[str.charAt(i + 1)]);
            if (charCount == 3) { num = (num << 6) | (base64[str.charAt(i + 2)]); }
            num = sign * num / 10;
            if (p % 2) {
                y = (num += y);
                params[p] = y + _y;
            }
            else {
                x = (num += x);
                params[p] = x + _x;
            }
            i += charCount;
        }
        f.apply(this, params);
    }
    return this;
};

createjs.Graphics.prototype.lineStyle = function (strength: number = NaN, color: string | number = 0, alpha?: number): createjs.Graphics {
    let rgbaColor = convertHexToRgba(color, alpha);

    this.setStrokeStyle(strength);
    this.beginStroke(rgbaColor);

    return this;
};

const originalBeginFill = createjs.Graphics.prototype.beginFill;
createjs.Graphics.prototype.beginFill = function (color: string | number, alpha?: number): createjs.Graphics {
    if (color != null) {
        color = convertHexToRgba(color, alpha);
    }
    originalBeginFill.apply(this, [color]);
    return this;
};

createjs.Graphics.prototype.beginGradientFill = function (gradientType: string,
    colors: Array<string>,
    alphas: Array<number>,
    ratios: Array<number>,
    matrix: createjs.Matrix2D | null = null,
    spreadMethod = "pad",
    interpolationMethod = "rgb",
    focalPointRatio = 0): void {
    // converting wrong values. It was OK in Flash to use numbers from 0 - 255, but createjs expects 0 - 1
    for (let i = 0; i < ratios.length; i++) {
        if (ratios[i] > 1) {
            ratios[i] = ratios[i] / 255;
        }

        if (alphas[i] > 1) {
            alphas[i] = alphas[i] / 255;
        }
    }

    const rgbColors = colors.map((color) => {
        return convertHexToRgba(color);
    });

    if (!matrix) {
        return;
    }
    // add Egrat.Matrix (AS3) compatible implementation, code from openfl::CanvasGraphic
    let m = matrix != null ? matrix.clone() : new createjs.Matrix2D();
    if (gradientType === "radial") {
        let p = matrix.transformPoint(1638.4, 0);
        this.beginRadialGradientFill(rgbColors, ratios, m.tx, m.ty, 0, m.tx, m.ty, (p.x - m.tx) / 2);
    } else {
        m.tx -= matrix.a * 1638.4 / 2;
        m.ty -= matrix.d * 1638.4 / 2;

        let p1 = m.transformPoint(0, 0);
        let p2 = m.transformPoint(1638.4, 0);
        this.beginLinearGradientFill(rgbColors, ratios, p1.x, p1.y, p2.x, p2.y);
    }
};

// image can't be a bitmap or bitmapData, but this is how we used it in BigFarm. Converting it here before calling "super".
const originalbeginBitmapFill = createjs.Graphics.prototype.beginBitmapFill;
createjs.Graphics.prototype.beginBitmapFill = function (image: Object, repetition?: string, matrix?: createjs.Matrix2D): Graphics {
    if (image instanceof createjs.Bitmap) {
        image = (image as createjs.Bitmap).bitmapData.canvas;
    }
    if (image instanceof createjs.BitmapData) {
        image = (image as createjs.BitmapData).canvas;
    }
    return originalbeginBitmapFill.apply(this, [image, repetition, matrix]);
}

createjs.Graphics.prototype.lineBitmapStyle = function (bitmap: BitmapData, matrix: createjs.Matrix2D | null = null, repeat: boolean = true, smooth: boolean = false): void {
    logger.warn("Graphics.lineBitmapStyle - is mocked method");
}

createjs.Graphics.prototype.copyFrom = function (sourceGraphics: createjs.Graphics): void {
    logger.warn("Please use graphics = Object.create(sourceGraphics); instead of copyFrom()");
}

const originalDrawRect = createjs.Graphics.prototype.drawRect;
createjs.Graphics.prototype.r = createjs.Graphics.prototype.drawRect = function (this: GraphicsWithBounds, x: number, y: number, w: number, h: number): createjs.Graphics {
    this._updateBounds(x, y, w, h);
    return originalDrawRect.apply(this, [x, y, w, h]);
}

const originalDrawRoundRect = createjs.Graphics.prototype.drawRoundRect;
createjs.Graphics.prototype.rr = createjs.Graphics.prototype.drawRoundRect = function (this: GraphicsWithBounds, x: number, y: number, w: number, h: number, r: number): createjs.Graphics {
    this._updateBounds(x, y, w, h);
    return originalDrawRoundRect.apply(this, [x, y, w, h, r]);
}

const originalLineTo = createjs.Graphics.prototype.lineTo;
createjs.Graphics.prototype.lt = createjs.Graphics.prototype.lineTo = function (x: number, y: number): createjs.Graphics {
    this.bounds.left = Math.min(this.bounds.left, x);
    this.bounds.right = Math.max(this.bounds.right, x);
    this.bounds.top = Math.min(this.bounds.top, y, y);
    this.bounds.bottom = Math.max(this.bounds.bottom, y);
    return originalLineTo.apply(this, [x, y]);
}

type GraphicsWithBounds = createjs.Graphics & { _bounds: createjs.Rectangle | undefined,_updateBounds: Function };

(createjs.Graphics.prototype as any)._updateBounds = function (this: GraphicsWithBounds, x: number, y: number, w: number, h: number) {
    if((this as any)._instructions.length || (this as any)._activeInstructions.length) {
        this.bounds.x = Math.min(this.bounds.x, x);
        this.bounds.width = Math.max(this.bounds.width, w);
        this.bounds.y = Math.min(this.bounds.y, y);
        this.bounds.height = Math.max(this.bounds.height, h);
    } else {
        this.bounds.x = x;
        this.bounds.width = w;
        this.bounds.y = y;
        this.bounds.height = h;
    }
}


Object.defineProperty(createjs.Graphics.prototype, "bounds", {
    get: function (this: GraphicsWithBounds): createjs.Rectangle {
        if (!this._bounds) {
            this._bounds = new createjs.Rectangle();
        }
        return this._bounds;
    },
    set: function (this: GraphicsWithBounds, value: createjs.Rectangle) {
        this._bounds = value;
    },
    enumerable: true,
    configurable: true
});

export { convertHexToRgba }
